import React, { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { motion } from 'framer-motion';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import { z } from 'zod';

import Button from '../../../../../../components/Button/Button';
import { cn } from '../../../../../../components/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../../../../components/ui/collapsible';
import {
  EItemBlockType,
  IBlockItem,
  IBlockItemFormProps,
  IRegularBlock
} from '../../../constants';
import { pageFormSchema } from '../../../validation-schema';

import { CtaItemForm } from './Forms/CtaItemForm';
import { ImageItemForm } from './Forms/ImageItemForm';
import { VideoItemForm } from './Forms/VideoItemForm';

interface ItemCardProps {
  item: IBlockItem;
  index: number;
  blockIndex: number;
  block: IRegularBlock;
}

const mapBlockTypeToForm: Partial<
  Record<EItemBlockType, React.FC<IBlockItemFormProps>>
> = {
  [EItemBlockType.IMAGE]: ImageItemForm,
  [EItemBlockType.VIDEO]: VideoItemForm,
  [EItemBlockType.CTA]: CtaItemForm
};

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  index,
  blockIndex,
  block
}) => {
  const { openItemIndex, setOpenItemIndex } = useGamePortalPagesState();
  const { watch, setValue } = useFormContext<z.infer<typeof pageFormSchema>>();
  const itemKey = `${blockIndex}-${index}`;
  const isExpanded = openItemIndex === itemKey;

  const itemType = item.type || block.type;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setOpenItemIndex(itemKey);
    } else {
      setOpenItemIndex(undefined);
    }
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const formBlocks = watch('blocksV2') as IRegularBlock[];
    const currentBlock = formBlocks[blockIndex];
    const currentItems = currentBlock.items || [];
    const newItems = currentItems.filter((_, i) => i !== index);

    const updatedBlocks = [...formBlocks];
    updatedBlocks[blockIndex] = {
      ...currentBlock,
      items: newItems
    };

    setValue('blocksV2', updatedBlocks, {
      shouldValidate: true,
      shouldDirty: true
    });

    if (openItemIndex === itemKey) {
      setOpenItemIndex(undefined);
    }
  };

  const BlockForm = useMemo(
    () => mapBlockTypeToForm[itemType as unknown as EItemBlockType] || null,
    [itemType]
  );

  const hasForm = itemType !== undefined;

  return (
    <Collapsible open={isExpanded} onOpenChange={handleOpenChange}>
      <div
        className={cn('rounded-md border border-base-border group/collapsible')}
      >
        <CollapsibleTrigger className="w-full">
          <div className="text-sm text-base-foreground font-semibold py-1 pl-4 pr-5 h-[61px] flex items-center justify-between rounded-md">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={`fa-light fa-arrow-turn-down-right`}
                className="text-xs text-base-mutedForeground"
              />
              <span>Item #{index + 1}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={handleDelete}
                className={cn(
                  'p-1 rounded group-hover/collapsible:opacity-100 hover:bg-gray-50 opacity-0 w-10 h-10 flex items-center justify-center'
                )}
                variant="ghost"
                title="Delete item"
              >
                <FontAwesomeIcon
                  icon="fa-regular fa-trash-can"
                  className="text-sm text-base-foreground"
                />
              </Button>
              {hasForm && (
                <FontAwesomeIcon
                  icon={`fa-solid ${isExpanded ? 'fa-minus' : 'fa-plus'}`}
                  className="text-xs text-base-mutedForeground font-normal"
                />
              )}
            </div>
          </div>
        </CollapsibleTrigger>
        {hasForm && (
          <motion.div
            layout
            initial={{ height: 0 }}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <CollapsibleContent>
              {BlockForm && (
                <div className="flex flex-col gap-4 px-5 pb-5">
                  <BlockForm
                    item={item}
                    blockIndex={blockIndex}
                    itemIndex={index}
                  />
                </div>
              )}
            </CollapsibleContent>
          </motion.div>
        )}
      </div>
    </Collapsible>
  );
};
